import * as React from 'react';
import { useTranslation } from 'react-i18next';

interface BookingInfoSectionProps {
  fundName: string | undefined;
}

export const BookingInfoSection: React.FC<BookingInfoSectionProps> = ({ fundName }) => {
  const { t } = useTranslation(['bookings', 'common']);

  // Only render if we have a fund name
  if (!fundName) return null;

  // DYPA specific content
  if (fundName === 'ΔΥΠΑ') {
    return (
      <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 my-4 text-sm relative">
        <div className="flex items-start">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="font-medium text-amber-800 mb-3">
              {t('bookingsBox.dypaInfo.reminder', { 
                ns: 'bookings', 
                defaultValue: 'Υπενθυμίζουμε ότι η συμπλήρωση της παραπάνω φόρμας δεν αποτελεί κατοχύρωση της εγγραφής σας.'
              })}
            </p>
            <p className="text-amber-800 mb-2">
              {t('bookingsBox.dypaInfo.steps', { 
                ns: 'bookings', 
                defaultValue: 'Θα πρέπει να ακολουθήσετε και τα εξής τρία (3) βήματα για την κατοχύρωση της θέσης σας:'
              })}
            </p>
            <ol className="list-decimal pl-5 space-y-2 text-amber-800">
              <li>
                {t('bookingsBox.dypaInfo.step1', { 
                  ns: 'bookings', 
                  defaultValue: 'Υποβολή ηλεκτρονικής αίτησης στην πλατφόρμα του ΔΥΠΑ (πρώην ΟΑΕΔ). Περισσότερες πληροφορίες'
                })} <a 
                  href="https://www.dypa.gov.gr/storage/kataskhnotiko-2024/odhgies-aithshs-dikaiuxu-2024.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-amber-600 underline hover:text-amber-800"
                >
                  {t('bookingsBox.dypaInfo.here', { ns: 'bookings', defaultValue: 'εδώ' })}
                </a>
              </li>
              <li>
                {t('bookingsBox.dypaInfo.step2', { 
                  ns: 'bookings', 
                  defaultValue: 'Αποστολή αριθμού πρωτοκόλλου: Μετά την υποβολή της αίτησης στον ΔΥΠΑ, θα πρέπει να μας κοινοποιήσετε τον αριθμό πρωτοκόλλου της. (Η μη κοινοποίηση του αριθμού πρωτοκόλλου ακυρώνει την προκράτηση της θέσης σας.)'
                })}
              </li>
              <li>
                {t('bookingsBox.dypaInfo.step3', { 
                  ns: 'bookings', 
                  defaultValue: 'Οριστική επιβεβαίωση θέσης: Μετά την ανακοίνωση των οριστικών αποτελεσμάτων και εφόσον έχετε εγκριθεί (δηλαδή, έχετε αριθμό επιταγής), θα πρέπει εντός 5 ημερών να επιβεβαιώσετε την προκράτησή σας στην κατασκήνωσή μας. (Θα λάβετε οδηγίες για τη διαδικασία.)'
                })}
              </li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  // Ιδιωτικά specific content
  if (fundName === 'Ιδιωτικά') {
    return (
      <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 my-4 text-sm relative">
        <div className="flex items-start">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="w-full">
            <p className="font-medium text-amber-800 mb-3">
              {t('bookingsBox.privateInfo.costIntro', { 
                ns: 'bookings', 
                defaultValue: 'Το κόστος συμμετοχής για κάθε 15νθήμερο κατασκηνωτικό πρόγραμμα αναγράφεται στον παρακάτω πίνακα.'
              })}
            </p>
            
            {/* Pricing Table */}
            <div className="overflow-x-auto mb-3">
              <table className="min-w-full border-collapse border border-amber-300 rounded">
                <thead>
                  <tr className="bg-amber-200">
                    <th className="py-2 px-3 text-left border border-amber-300 font-medium text-amber-900">
                      {t('bookingsBox.privateInfo.period', { ns: 'bookings', defaultValue: 'Κατασκηνωτική περίοδος 2025' })}
                    </th>
                    <th className="py-2 px-3 text-left border border-amber-300 font-medium text-amber-900">
                      {t('bookingsBox.privateInfo.cost', { ns: 'bookings', defaultValue: 'Κόστος' })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-amber-50 hover:bg-amber-100">
                    <td className="py-2 px-3 border border-amber-300 text-amber-800">
                      {t('bookingsBox.privateInfo.periodA', { ns: 'bookings', defaultValue: 'Α κατασκηνωτική περίοδος' })}
                    </td>
                    <td className="py-2 px-3 border border-amber-300 text-amber-800 font-medium">
                      630€
                    </td>
                  </tr>
                  <tr className="bg-amber-50 hover:bg-amber-100">
                    <td className="py-2 px-3 border border-amber-300 text-amber-800">
                      {t('bookingsBox.privateInfo.periodB', { ns: 'bookings', defaultValue: 'Β κατασκηνωτική περίοδος' })}
                    </td>
                    <td className="py-2 px-3 border border-amber-300 text-amber-800 font-medium">
                      660€
                    </td>
                  </tr>
                  <tr className="bg-amber-50 hover:bg-amber-100">
                    <td className="py-2 px-3 border border-amber-300 text-amber-800">
                      {t('bookingsBox.privateInfo.periodC', { ns: 'bookings', defaultValue: 'Γ κατασκηνωτική περίοδος' })}
                    </td>
                    <td className="py-2 px-3 border border-amber-300 text-amber-800 font-medium">
                      660€
                    </td>
                  </tr>
                  <tr className="bg-amber-50 hover:bg-amber-100">
                    <td className="py-2 px-3 border border-amber-300 text-amber-800">
                      {t('bookingsBox.privateInfo.periodD', { ns: 'bookings', defaultValue: 'Δ κατασκηνωτική περίοδος' })}
                    </td>
                    <td className="py-2 px-3 border border-amber-300 text-amber-800 font-medium">
                      630€
                    </td>
                  </tr>
                  <tr className="bg-amber-50 hover:bg-amber-100">
                    <td className="py-2 px-3 border border-amber-300 text-amber-800">
                      {t('bookingsBox.privateInfo.periodE', { ns: 'bookings', defaultValue: 'Ε κατασκηνωτική περίοδος' })}
                    </td>
                    <td className="py-2 px-3 border border-amber-300 text-amber-800 font-medium">
                      580€
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="mt-3">
              <p className="font-medium text-amber-800 mb-2">
                {t('bookingsBox.privateInfo.note', { ns: 'bookings', defaultValue: 'Παρατήρηση:' })}
              </p>
              <ul className="list-disc pl-5 space-y-1 text-amber-800">
                <li>
                  {t('bookingsBox.privateInfo.discount1', { 
                    ns: 'bookings', 
                    defaultValue: 'Για δύο αδέλφια, παρέχεται συνολική έκπτωση 10%.'
                  })}
                </li>
                <li>
                  {t('bookingsBox.privateInfo.discount2', { 
                    ns: 'bookings', 
                    defaultValue: 'Για τρία ή περισσότερα αδέλφια, παρέχεται συνολική έκπτωση 15%.'
                  })}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ΕΦΚΑ specific content
  if (fundName === 'ΕΦΚΑ') {
    return (
      <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 my-4 text-sm relative">
        <div className="flex items-start">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="font-medium text-amber-800 mb-3">
              {t('bookingsBox.efkaInfo.reminder', { 
                ns: 'bookings', 
                defaultValue: 'Υπενθυμίζουμε ότι η συμπλήρωση της παραπάνω φόρμας δεν αποτελεί κατοχύρωση της συμμετοχής σας.'
              })}
            </p>
            
            <p className="text-amber-800 mb-3">
              {t('bookingsBox.efkaInfo.declaration', { 
                ns: 'bookings', 
                defaultValue: 'Η δήλωση της κατασκήνωσης και της περιόδου που επιθυμείτε γίνεται αποκλειστικά μέσω των ηλεκτρονικών υπηρεσιών του ΕΦΚΑ. Ο ΕΦΚΑ είναι ο αρμόδιος φορέας για τη συμμετοχή σας στο κατασκηνωτικό πρόγραμμα.'
              })}
            </p>
            
            <p className="text-amber-800 mb-3">
              {t('bookingsBox.efkaInfo.disclaimer', { 
                ns: 'bookings', 
                defaultValue: 'Η παρούσα εκδήλωση ενδιαφέροντος αφορά αποκλειστικά εσωτερικούς διαδικαστικούς λόγους της κατασκήνωσης και δεν είναι δεσμευτική. Η κατασκήνωση θα δεχτεί ως έγκυρες μόνο τις κρατήσεις που έχουν εγκριθεί από τον ΕΦΚΑ, οι οποίες θα μας κοινοποιηθούν με τη λήξη της περιόδου των εγγραφών.'
              })}
            </p>
            
            <div className="bg-amber-100 p-3 rounded-md border border-amber-300 mb-3">
              <p className="text-amber-800 flex items-center">
                <span className="text-amber-600 mr-2">🔹</span>
                <span>
                  {t('bookingsBox.efkaInfo.parentReminder', { 
                    ns: 'bookings', 
                    defaultValue: 'Υπενθύμιση: Ο ασφαλισμένος γονέας πρέπει απαραίτητα να δηλώσει συμμετοχή στο κατασκηνωτικό πρόγραμμα του ΕΦΚΑ για να εγκριθεί.'
                  })}
                </span>
              </p>
            </div>
            
            <div className="bg-amber-100 p-3 rounded-md border border-amber-300">
              <p className="text-amber-800 flex items-center">
                <span className="text-amber-600 mr-2">⚠️</span>
                <span>
                  {t('bookingsBox.efkaInfo.important', { 
                    ns: 'bookings', 
                    defaultValue: 'Σημαντικό: Αν είστε ιδιωτικός υπάλληλος, ο κατασκηνωτικός σας φορέας ΔΕΝ είναι ο ΕΦΚΑ, αλλά πιθανώς ο ΟΑΕΔ (ΔΥΠΑ).'
                  })}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Λοιποί φορείς και Συνεργασίες specific content
  if (fundName === 'Λοιποί φορείς και Συνεργασίες') {
    return (
      <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 my-4 text-sm relative">
        <div className="flex items-start">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="font-medium text-amber-800 mb-3">
              {t('bookingsBox.otherFundsInfo.reminder', { 
                ns: 'bookings', 
                defaultValue: 'Υπενθυμίζουμε ότι η συμπλήρωση της παραπάνω φόρμας δεν αποτελεί κατοχύρωση της εγγραφής σας.'
              })}
            </p>
            
            <p className="text-amber-800">
              {t('bookingsBox.otherFundsInfo.communication', { 
                ns: 'bookings', 
                defaultValue: 'Πριν από την εγγραφή, θα πρέπει να προηγηθεί επικοινωνία με εκπρόσωπο της κατασκήνωσης, καθώς ο φορέας σας ενδέχεται να απαιτεί πρόσθετες διαδικασίες εγγραφής.'
              })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}; 